import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { RigidRig } from "../rigidRig";
import { HqMaterials } from "../hqMaterials";
import { Lod } from "./lod";

/** How a bake treats its meshes. */
export interface BakeOptions {
  /** Whether the merged meshes cast shadows: props on the map do; scenery in the haze does not. */
  readonly shadows: boolean;
  /**
   * A reduced-detail target for each prop (a direct child of the source), or null to keep it whole: the share of its
   * triangles to keep and the error allowed, relative to its size (lod.ts).
   */
  readonly lod?: (prop: THREE.Object3D) => { ratio: number; error: number } | null;
  /** Filled with where each prop's vertices landed, so a prop can later be hidden in place (`hideBaked`). */
  readonly ranges?: Map<THREE.Object3D, BakedRange[]>;
}

/** A run of one baked prop's vertices inside a merged mesh. */
export interface BakedRange { readonly mesh: THREE.Mesh; readonly start: number; readonly count: number; saved: Float32Array | null }

/**
 * Collapses a tree of static meshes into a handful of merged meshes: one per material, per attribute layout, per
 * spatial chunk.
 *
 * The land beyond the map is scenery with no job — nothing is placed there, nothing animates by clip — and it was
 * ~2,200 separate meshes (every tree is a trunk and a stack of foliage tiers), each its own draw call, twice over
 * with the shadow pass. A phone spends its frame submitting them. Merged, the same scenery is a few dozen draws.
 * Chunking keeps frustum culling useful: one mesh spanning the whole world is always "on screen".
 *
 * Geometry is copied to plain float attributes in the target's space (the kit ships quantized, normalized int16
 * positions with the dequantizing scale in the node matrix, and those cannot be transformed in place). Meshes this
 * cannot merge — skinned, instanced, multi-material — are moved across unchanged. Returns the merged meshes, whose
 * geometry the caller owns and disposes.
 */
export function bakeStatic(source: THREE.Object3D, target: THREE.Object3D, chunk: number, options: BakeOptions): THREE.Mesh[] {
  source.updateMatrixWorld(true);
  target.updateMatrixWorld(true);
  const toTarget = new THREE.Matrix4().copy(target.matrixWorld).invert();
  const buckets = new Map<string, { material: THREE.Material; geos: THREE.BufferGeometry[]; props: THREE.Object3D[] }>();
  const loose: THREE.Object3D[] = [];
  const p = new THREE.Vector3();
  source.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.visible) return;
    if ((mesh as THREE.InstancedMesh).isInstancedMesh || Array.isArray(mesh.material)) { loose.push(mesh); return; }
    let geo: THREE.BufferGeometry;
    let material = mesh.material;
    if ((mesh as THREE.SkinnedMesh).isSkinnedMesh) {
      // a tree merged into a rigid rig (rigidRig.ts): bake each part where its bone stands. It draws with a twin of
      // its material, because the same material on the skinned trees inside the map would switch programs per draw
      const skin = mesh as THREE.SkinnedMesh;
      const baked = RigidRig.bake(skin, toTarget, floatCopy);
      if (!baked) { loose.push(mesh); return; }
      geo = baked;
      material = HqMaterials.twin(mesh.material);
      const bones = skin.skeleton.bones, m = new THREE.Matrix4(), index = skin.geometry.getAttribute("skinIndex");
      const scaleOf = bones.map((bone) => m.multiplyMatrices(toTarget, bone.matrixWorld).getMaxScaleOnAxis());
      if (sways(material)) keepWindScale(geo, (i) => scaleOf[index.getX(i)]);
    } else {
      const m = new THREE.Matrix4().multiplyMatrices(toTarget, mesh.matrixWorld);
      geo = floatCopy(mesh.geometry);
      geo.applyMatrix4(m);
      const s = m.getMaxScaleOnAxis();
      if (sways(material)) keepWindScale(geo, () => s);
    }
    const prop = propOf(mesh, source);
    const lod = options.lod?.(prop);
    if (lod) {
      // every copy of one source geometry bakes to the same vertex order, so the first copy's reduction serves them all
      const r = Lod.reduction(mesh.geometry, geo, lod.ratio, lod.error);
      if (r) { const reduced = Lod.apply(geo, r); geo.dispose(); geo = reduced; }
    }
    mesh.getWorldPosition(p);
    const layout = Object.keys(geo.attributes).sort().map((k) => `${k}${geo.getAttribute(k).itemSize}`).join(",");
    const key = `${material.uuid}|${layout}|${Math.floor(p.x / chunk)},${Math.floor(p.z / chunk)}`;
    let b = buckets.get(key);
    if (!b) { b = { material, geos: [], props: [] }; buckets.set(key, b); }
    b.geos.push(geo);
    b.props.push(prop);
  });
  const made: THREE.Mesh[] = [];
  for (const { material, geos, props } of buckets.values()) {
    const counts = geos.map((g) => g.getAttribute("position").count);
    const merged = mergeGeometries(geos, false);
    for (const g of geos) g.dispose();
    if (!merged) continue;
    merged.computeBoundingSphere();
    const m = new THREE.Mesh(merged, material);
    m.castShadow = options.shadows;
    m.receiveShadow = true;
    if (options.ranges) {
      let start = 0;
      counts.forEach((count, i) => {
        const list = options.ranges!.get(props[i]) ?? options.ranges!.set(props[i], []).get(props[i])!;
        list.push({ mesh: m, start, count, saved: null });
        start += count;
      });
    }
    m.matrixAutoUpdate = false;
    target.add(m);
    made.push(m);
  }
  for (const o of loose) target.attach(o);
  return made;
}

/**
 * Hides or shows one baked prop in place: its vertices are dropped well under the ground (and put back), a buffer
 * write of that prop's range and nothing else. Collapsing them to one point is not enough — the wind shader moves each
 * vertex by its own weight, which would open the collapsed triangles back up into specks.
 */
export function hideBaked(ranges: readonly BakedRange[], hidden: boolean): void {
  for (const r of ranges) {
    const pos = r.mesh.geometry.getAttribute("position") as THREE.BufferAttribute;
    const arr = pos.array as Float32Array, from = r.start * 3, to = from + r.count * 3;
    if (hidden) {
      if (r.saved) continue;
      r.saved = arr.slice(from, to);
      for (let i = from + 1; i < to; i += 3) arr[i] -= HIDE_DEPTH;
    } else {
      if (!r.saved) continue;
      arr.set(r.saved, from);
      r.saved = null;
    }
    pos.addUpdateRange(from, to - from);
    pos.needsUpdate = true;
  }
}
/** How far under the ground a hidden baked prop is dropped: below any terrain and outside the shadow camera. */
const HIDE_DEPTH = 100;

/**
 * Keeps the wind's sway the size it was before baking. The wind shader (hqMaterials.ts) displaces a vertex in its
 * mesh's own space by its weight (COLOR_0 red), so a prop drawn through its node's scale swayed by that scale; baked,
 * its vertices are already in world space, and the same weight would swing it by the unscaled amount. Scaling the
 * weight by what the node's transform did keeps the motion as it was.
 */
function keepWindScale(geo: THREE.BufferGeometry, scaleAt: (vertex: number) => number): void {
  const color = geo.getAttribute("color") as THREE.BufferAttribute | undefined;
  if (!color) return;
  for (let i = 0; i < color.count; i++) color.setX(i, color.getX(i) * scaleAt(i));
}

/** Whether a material carries the wind (hqMaterials.ts marks its program key); only then is COLOR_0 a wind weight. */
function sways(material: THREE.Material): boolean {
  return material.customProgramCacheKey?.().includes("td-wind") ?? false;
}

/** The direct child of `root` that `o` belongs to (the placed prop). */
function propOf(o: THREE.Object3D, root: THREE.Object3D): THREE.Object3D {
  let q = o;
  while (q.parent && q.parent !== root) q = q.parent;
  return q;
}

/** An indexed copy of a geometry with every attribute as plain, non-normalized float32. */
function floatCopy(src: THREE.BufferGeometry): THREE.BufferGeometry {
  const out = new THREE.BufferGeometry();
  for (const name of Object.keys(src.attributes)) {
    const a = src.getAttribute(name);
    const n = a.count, s = a.itemSize, arr = new Float32Array(n * s);
    for (let i = 0; i < n; i++) {
      arr[i * s] = a.getX(i);
      if (s > 1) arr[i * s + 1] = a.getY(i);
      if (s > 2) arr[i * s + 2] = a.getZ(i);
      if (s > 3) arr[i * s + 3] = a.getW(i);
    }
    out.setAttribute(name, new THREE.BufferAttribute(arr, s));
  }
  if (src.index) out.setIndex(Array.from(src.index.array as ArrayLike<number>));
  else out.setIndex(Array.from({ length: src.getAttribute("position").count }, (_, i) => i));
  return out;
}
